// @ts-nocheck
import axios from 'axios';
import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import API_ENDPOINTS from '../const/ApiEndPoints.js';
import { getAllAttributeList } from '../hooks/Configuration/AttributeLibrary/useAttributes.jsx';
import { getEmailTemplate } from '../hooks/Configuration/useEmailTempate.js';
import { getAllSummarySections } from '../hooks/Configuration/useSummary.js';
import { useQueryEngine } from './useDataEngine.jsx';
import { getAllConstantAttributesList } from '../hooks/Configuration/ConstantAttributes/useConstantAttributes.jsx';
import { getChangeRequestFieldOptions } from '../hooks/ModelInventory/useChangeRequestImpact.js';
import { getRuleList } from '../hooks/RulesAndReports/rulesApi.js';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../const/CommonConst.js';

const STALE_TIME = 5 * 60 * 1000;
const GC_TIME = 30 * 60 * 1000;

const ATTRIBUTE_BASE_KEY = ['attributes', 'lists'];
const CONSTANT_ATTRIBUTE_BASE_KEY = ['constantAttributes', 'lists'];
const EMAIL_TEMPLATE_BASE_KEY = ['emailTemplates', 'lists'];
const TEMPLATE_BASE_KEY = ['templates', 'lists'];
const CHANGE_REQUEST_FIELD_OPTIONS_BASE_KEY = ['FieldChangeActions', 'lists'];
const SUMMARY_SECTION_BASE_KEY = ['summarySections', 'lists'];
const RULE_LIST_BASE_KEY = ['rules', 'lists'];
const INVENTORY_VIEW_LIST_BASE_KEY = ['inventoryViews', 'lists'];

const customEntityList = (body, loader = true) => {
  return axios
    .get(
      `${API_ENDPOINTS.MODULE_BASE_URL.AUTH}${API_ENDPOINTS.CE.GET_TEMPLATE_LIST}`,
      { params: body, loader }
    )
    .then((res) => {
      return res;
    })
    .catch((err) => {
      throw err;
    });
};

const getInventoryViewList = (body, loader = true) => {
  return axios
    .post(
      `${API_ENDPOINTS.MODULE_BASE_URL.AUTH}${API_ENDPOINTS.MI.GET_INVENTORY_VIEW}`,
      body,
      {
        loader,
      }
    )
    .then((res) => {
      return res;
    })
    .catch((err) => {
      throw err;
    });
};

const getQueryKey = (baseKey, params) => [
  ...baseKey,
  JSON.stringify(params ?? {}),
];

const invalidateQueryCache = (queryClient, { params = {}, baseKey }) => {
  const specificKey = getQueryKey(baseKey, params);

  // Always invalidate the active cache entry, including the default {} params key.
  queryClient.invalidateQueries({ queryKey: specificKey, exact: true });

  // remove cache of all OTHER param variations except the current params
  queryClient
    .getQueryCache()
    .findAll({ queryKey: baseKey, exact: false })
    .forEach((query) => {
      const queryParams = query.queryKey[baseKey.length];
      /**
       * we have invalidated the current param(specificKey) using invalidateQueries above,
       * now we are removing caching data of all different params, except the current params
       */
      if (queryParams !== JSON.stringify(params)) {
        queryClient.removeQueries({ queryKey: query.queryKey });
      }
    });
};

const getQueryOptions = ({ enabled, refetchOnWindowFocus }) => {
  return {
    enabled,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    refetchOnMount: false,
    refetchOnWindowFocus: enabled ? refetchOnWindowFocus : false,
    refetchOnReconnect: false,
    placeholderData: (prev) => prev,
  };
};

const useConfigListQuery = ({
  baseKey,
  fetchData,
  fallbackData = EMPTY_ARRAY,
  enabled = true,
  params = {},
  refetchOnWindowFocus = true,
} = {}) => {
  const queryClient = useQueryClient();
  const serializedParams = JSON.stringify(params ?? {});
  const queryKey = useMemo(
    () => getQueryKey(baseKey, params),
    [baseKey, serializedParams]
  );

  const queryFn = useCallback(
    () => fetchData(params).then((res) => res?.data || fallbackData),
    [fetchData, params, fallbackData]
  );

  const query = useQueryEngine(
    queryKey,
    queryFn,
    getQueryOptions({
      enabled,
      refetchOnWindowFocus,
    })
  );

  const invalidateQuery = useCallback(
    (invalidateParams = params) =>
      invalidateQueryCache(queryClient, {
        params: invalidateParams,
        baseKey,
      }),
    [baseKey, params, queryClient]
  );

  const ensureQueryData = useCallback(
    () =>
      queryClient.ensureQueryData({
        queryKey,
        queryFn,
        staleTime: STALE_TIME,
        gcTime: GC_TIME,
      }),
    [queryClient, queryFn, queryKey]
  );

  return {
    ...query,
    invalidateQuery,
    ensureQueryData,
  };
};

const useAttributeListQuery = ({
  enabled = true,
  params = {},
  refetchOnWindowFocus = true,
  loader = true,
} = {}) => {
  const {
    invalidateQuery: invalidateAttributes,
    ensureQueryData: fetchAttributes,
    ...query
  } = useConfigListQuery({
    baseKey: ATTRIBUTE_BASE_KEY,
    fetchData: (queryParams) => getAllAttributeList(queryParams, loader),
    fallbackData: EMPTY_ARRAY,
    enabled,
    params,
    refetchOnWindowFocus,
  });

  return { ...query, fetchAttributes, invalidateAttributes };
};

const useConstantAttributeListQuery = ({
  enabled = true,
  params = {},
  refetchOnWindowFocus = true,
} = {}) => {
  const {
    invalidateQuery: invalidateConstantAttributes,
    ensureQueryData: fetchConstantAttributes,
    ...query
  } = useConfigListQuery({
    baseKey: CONSTANT_ATTRIBUTE_BASE_KEY,
    fetchData: getAllConstantAttributesList,
    fallbackData: EMPTY_ARRAY,
    enabled,
    params,
    refetchOnWindowFocus,
  });

  return { ...query, fetchConstantAttributes, invalidateConstantAttributes };
};

const useEmailTemplateListQuery = ({
  enabled = true,
  params = {},
  refetchOnWindowFocus = true,
} = {}) => {
  const {
    invalidateQuery: invalidateEmailTemplates,
    ensureQueryData: fetchEmailTemplates,
    ...query
  } = useConfigListQuery({
    baseKey: EMAIL_TEMPLATE_BASE_KEY,
    fetchData: getEmailTemplate,
    fallbackData: EMPTY_ARRAY,
    enabled,
    params,
    refetchOnWindowFocus,
  });

  return { ...query, fetchEmailTemplates, invalidateEmailTemplates };
};

const useTemplateListQuery = ({
  enabled = true,
  params = {},
  refetchOnWindowFocus = true,
} = {}) => {
  const {
    invalidateQuery: invalidateTemplates,
    ensureQueryData: fetchTemplates,
    ...query
  } = useConfigListQuery({
    baseKey: TEMPLATE_BASE_KEY,
    fetchData: customEntityList,
    fallbackData: EMPTY_ARRAY,
    enabled,
    params,
    refetchOnWindowFocus,
  });

  return { ...query, fetchTemplates, invalidateTemplates };
};

const useChangeRequestFieldOptionsQuery = ({
  enabled = true,
  params = {},
  refetchOnWindowFocus = true,
} = {}) => {
  // components using enabled:false can call this(ensureQueryData) to trigger the first fetch;
  // subsequent calls return cached data without hitting the API.
  const {
    invalidateQuery: invalidateChangeRequestFieldOptions,
    ensureQueryData: fetchChangeRequestFieldOptions,
    ...query
  } = useConfigListQuery({
    baseKey: CHANGE_REQUEST_FIELD_OPTIONS_BASE_KEY,
    fetchData: getChangeRequestFieldOptions,
    fallbackData: EMPTY_ARRAY,
    enabled,
    params,
    refetchOnWindowFocus,
  });

  return {
    ...query,
    fetchChangeRequestFieldOptions,
    invalidateChangeRequestFieldOptions,
  };
};

const useSummarySectionListQuery = ({
  enabled = true,
  params = {},
  refetchOnWindowFocus = true,
} = {}) => {
  // components using enabled:false can call this(ensureQueryData) to trigger the first fetch;
  // subsequent calls return cached data without hitting the API.
  const {
    invalidateQuery: invalidateSummarySections,
    ensureQueryData: fetchSummarySections,
    ...query
  } = useConfigListQuery({
    baseKey: SUMMARY_SECTION_BASE_KEY,
    fetchData: () => getAllSummarySections(false),
    fallbackData: EMPTY_ARRAY,
    enabled,
    params,
    refetchOnWindowFocus,
  });
  return { ...query, fetchSummarySections, invalidateSummarySections };
};

const useRuleListQuery = ({
  enabled = true,
  params = {},
  refetchOnWindowFocus = true,
} = {}) => {
  // components using enabled:false can call this(ensureQueryData) to trigger the first fetch;
  // subsequent calls return cached data without hitting the API.
  const {
    invalidateQuery: invalidateRules,
    ensureQueryData: fetchRules,
    ...query
  } = useConfigListQuery({
    baseKey: RULE_LIST_BASE_KEY,
    fetchData: (queryParams) => getRuleList(queryParams, false),
    fallbackData: EMPTY_ARRAY,
    enabled,
    params,
    refetchOnWindowFocus,
  });
  return { ...query, fetchRules, invalidateRules };
};

const useInventoryViewListQuery = ({
  enabled = true,
  params = {},
  refetchOnWindowFocus = true,
} = {}) => {
  // components using enabled:false can call this(ensureQueryData) to trigger the first fetch;
  // subsequent calls return cached data without hitting the API.
  const {
    invalidateQuery: invalidateInventoryViews,
    ensureQueryData: fetchInventoryViews,
    ...query
  } = useConfigListQuery({
    baseKey: INVENTORY_VIEW_LIST_BASE_KEY,
    fetchData: (queryParams) => getInventoryViewList(queryParams, false),
    fallbackData: EMPTY_OBJECT,
    enabled,
    params,
    refetchOnWindowFocus,
  });
  return { ...query, fetchInventoryViews, invalidateInventoryViews };
};

export {
  useAttributeListQuery,
  useEmailTemplateListQuery,
  useTemplateListQuery,
  useConstantAttributeListQuery,
  useChangeRequestFieldOptionsQuery,
  useSummarySectionListQuery,
  useRuleListQuery,
  useInventoryViewListQuery,
};
